import { Mail, ShieldCheck } from "lucide-react";
import PropTypes from "prop-types";
import LoadingButton from "@components/ui-components/loading-button";
import Modal from "@components/ui-components/modal";
import Select from "@components/ui-components/select";

// `brands` lists who the reminder can go out under; a picker only shows when
// there's a real choice. Sending (and closing on success) belongs to the caller.
const ReminderPreviewModal = ({ open, onClose, brandName, brands = [], brandId, onBrandChange, recipients = [], onSend, sending }) => {
    const [primary] = recipients;
    const isBulk = recipients.length > 1;

    return (
        <Modal
            open={open}
            onClose={onClose}
            width="md"
            title="KYC reminder preview"
            subtitle={isBulk
                ? `This is what ${recipients.length} incomplete publishers will receive.`
                : `This is what ${primary?.name} will receive.`}
            footer={(
                <>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                        Cancel
                    </button>
                    <LoadingButton
                        size="lg"
                        icon={Mail}
                        onClick={onSend}
                        loading={sending}
                        disabled={!brandId}
                        loadingText="Sending…"
                        text="Send reminder" />
                </>
            )}>
            {brands.length > 1 && (
                <div className="mb-4">
                    <Select
                        id="reminder-brand"
                        label="Send on behalf of"
                        value={brandId}
                        isClearable={false}
                        options={brands.map((brand) => ({ value: brand.id, label: brand.name || brand.id }))}
                        onChange={onBrandChange} />
                </div>
            )}
            {primary && (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs text-muted">
                        <Mail className="h-3.5 w-3.5" />
                        Email preview
                    </div>
                    <div className="space-y-1 border-b border-gray-100 px-4 py-3 text-xs text-muted">
                        <p><span className="font-medium text-gray-700">From:</span> {brandName} &lt;notifications@nookpay.tech&gt;</p>
                        <p><span className="font-medium text-gray-700">To:</span> {primary.name} &lt;{primary.email}&gt;</p>
                        <p><span className="font-medium text-gray-700">Subject:</span> Reminder: Complete your KYC to receive your {brandName} payout</p>
                    </div>
                    <div className="px-5 py-5">
                        <p className="mb-4 text-sm font-semibold text-gray-900">Hi {primary.name},</p>
                        <p className="mb-4 text-sm text-gray-700">
                            {brandName} has ₹{Number(primary.pending_amount || 0).toLocaleString("en-IN")} in pending earnings for you.
                            To release this payout, please complete your KYC verification with Nook Pay.
                        </p>
                        <div className="rounded-lg border border-gray-200 bg-light p-4 text-sm text-gray-700">
                            <p className="mb-2 flex items-center gap-2 font-medium text-gray-900">
                                <ShieldCheck className="h-4 w-4 text-info" />
                                What you&apos;ll need
                            </p>
                            <ul className="list-inside list-disc space-y-1 text-sm text-gray-700">
                                <li>PAN card number</li>
                                <li>Aadhaar number (with mobile OTP)</li>
                                <li>Bank account details for payout</li>
                            </ul>
                            <p className="mt-2 text-xs text-muted">Takes about 3 minutes. Verified instantly by Surepass.</p>
                        </div>
                        <button
                            type="button"
                            disabled
                            className="mt-4 w-full cursor-default rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white">
                            Complete KYC now →
                        </button>
                        <p className="mt-4 text-xs text-muted">
                            Once verified, KYC is reused across every brand you work with on Nook Pay — you only do this once.
                        </p>
                    </div>
                </div>
            )}
        </Modal>
    );
};

ReminderPreviewModal.propTypes = {
    open         : PropTypes.bool.isRequired,
    onClose      : PropTypes.func.isRequired,
    brandName    : PropTypes.string.isRequired,
    brands       : PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
    brandId      : PropTypes.string,
    onBrandChange: PropTypes.func,
    recipients   : PropTypes.arrayOf(PropTypes.shape({
        name          : PropTypes.string,
        email         : PropTypes.string,
        pending_amount: PropTypes.number
    })),
    onSend : PropTypes.func.isRequired,
    sending: PropTypes.bool
};

export default ReminderPreviewModal;
